import { toWorldPixel } from 'components/common/map/webgl/webMercator'

interface MarkerFeature {
  id?: string
  geometry?: { coordinates?: number[] } | null
  properties?: { id?: string; sequence?: number | string | null } | null
}

interface CollisionOptions {
  zoom: number
  collisionPx?: number
  baseOffset?: number
  // Order within a cluster. Containers rank by serial rather than sequence, so
  // the caller supplies the key instead of every marker shape growing a `sequence`.
  sortKey?: (marker: MarkerFeature) => string
}

type OffsetMap = Record<string, { lat: number; lng: number }>

interface SpiralNode {
  marker: MarkerFeature
  coords: number[]
  px: number
  py: number
  bx: number
  by: number
  idx: number
  parent: number
}

// Two pills whose centres sit closer than this many screen pixels overlap
// visually; derived from the pill footprint (PILL_CSS_HEIGHT ≈ 35).
const DEFAULT_COLLISION_PX = 35

const goldenAngle = Math.PI * (3 - Math.sqrt(5))

// Vogel/sunflower spiral in meters, used for every cluster size so the gap
// between neighbouring markers stays a consistent ~`baseOffset` meters regardless
// of how many collide — the separation is fixed per offset and never drifts with
// cluster size (nor, therefore, with zoom).
const computeSlots = (count: number, baseOffset: number) =>
  Array.from({ length: count }, (_, i) => ({
    radius: baseOffset * Math.sqrt((i + 1) / Math.PI),
    angle: (i + 1) * goldenAngle,
  }))

const coordsOf = (marker: MarkerFeature): number[] | null => {
  const c = marker.geometry && marker.geometry.coordinates
  return c && c.length >= 2 ? c : null
}
const idOf = (marker: MarkerFeature): string | undefined => marker.id || (marker.properties ? marker.properties.id : undefined)

// Order within a cluster so the spiral is meaningful (lowest sequence innermost),
// falling back to the stable id when a sequence isn't available.
const defaultSortKey = (marker: MarkerFeature): string => {
  const seq = marker.properties ? marker.properties.sequence : null
  if (seq != null && seq !== '' && !Number.isNaN(Number(seq))) {
    return `0:${Number(seq).toString().padStart(12, '0')}`
  }
  return `1:${idOf(marker) || ''}`
}

/**
 * Collision-aware marker offsets ("spiderfy"): groups markers whose pills overlap
 * on screen at the current zoom, then nudges each member from its own coordinate
 * along a meters-based spiral (a fixed per-offset gap). Non-colliding markers, and
 * every marker when `baseOffset` is 0, keep their original coordinates.
 */
export const calculateCollisionOffsets = (
  markers: MarkerFeature[],
  { zoom, collisionPx = DEFAULT_COLLISION_PX, baseOffset = 0, sortKey = defaultSortKey }: CollisionOptions = { zoom: 0 }
): OffsetMap => {
  const offsetMap: OffsetMap = {}
  if (!Array.isArray(markers) || markers.length === 0) return offsetMap
  // collisionPx divides the pixel grid, so a 0/negative/NaN value would yield
  // Infinity/NaN bucket keys — bail on it alongside the baseOffset/zoom checks.
  if (!(baseOffset > 0) || !Number.isFinite(zoom) || !(collisionPx > 0)) return offsetMap

  // Project each marker to world pixels and bucket it into a collisionPx grid so
  // neighbour lookups only touch the 3×3 window around each cell.
  const nodes: SpiralNode[] = []
  const grid = new Map<string, SpiralNode[]>()
  const cellKey = (bx: number, by: number) => `${bx},${by}`
  markers.forEach(marker => {
    const coords = coordsOf(marker)
    if (coords) {
      const [px, py] = toWorldPixel(coords[0], coords[1], zoom)
      const bx = Math.floor(px / collisionPx)
      const by = Math.floor(py / collisionPx)
      const node: SpiralNode = { marker, coords, px, py, bx, by, idx: nodes.length, parent: nodes.length }
      nodes.push(node)
      const bucket = grid.get(cellKey(bx, by))
      if (bucket) bucket.push(node)
      else grid.set(cellKey(bx, by), [node])
    }
  })

  const find = (i: number): number => {
    let root = i
    while (nodes[root].parent !== root) root = nodes[root].parent
    let n = i
    while (nodes[n].parent !== n) {
      const next = nodes[n].parent
      nodes[n].parent = root
      n = next
    }
    return root
  }
  const union = (a: number, b: number) => {
    const ra = find(a)
    const rb = find(b)
    if (ra !== rb) nodes[rb].parent = ra
  }

  const collideSq = collisionPx * collisionPx
  nodes.forEach(node => {
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const bucket = grid.get(cellKey(node.bx + dx, node.by + dy))
        if (bucket) {
          bucket.forEach(other => {
            if (other !== node) {
              const ddx = node.px - other.px
              const ddy = node.py - other.py
              if (ddx * ddx + ddy * ddy < collideSq) union(node.idx, other.idx)
            }
          })
        }
      }
    }
  })

  const clusters = new Map<number, SpiralNode[]>()
  nodes.forEach(node => {
    const root = find(node.idx)
    const group = clusters.get(root)
    if (group) group.push(node)
    else clusters.set(root, [node])
  })

  const metersPerLat = 111111
  clusters.forEach(group => {
    if (group.length <= 1) return
    const sorted = [...group].sort((a, b) => sortKey(a.marker).localeCompare(sortKey(b.marker)))
    const slots = computeSlots(sorted.length, baseOffset)

    // Offset each marker from its OWN coordinate (like the exact-coord original),
    // converting meters→degrees at that marker's own latitude, so every stop stays
    // anchored to its true position instead of drifting toward a centroid.
    sorted.forEach((node, index) => {
      const { radius, angle } = slots[index]
      const [lng, lat] = node.coords
      // cos(lat) → 0 near the poles; floor the denominator so dLng can't blow up
      // to Infinity/NaN and feed an invalid coordinate to Google Maps.
      const metersPerLng = Math.max(111111 * Math.cos(lat * (Math.PI / 180)), 1e-6)
      const dLat = (radius * Math.cos(angle)) / metersPerLat
      const dLng = (radius * Math.sin(angle)) / metersPerLng
      const id = idOf(node.marker)
      if (id) offsetMap[id] = { lat: lat + dLat, lng: lng + dLng }
    })
  })

  return offsetMap
}
